// This file provides react hooks for using them with typescript

import { DependencyList, useEffect, useRef } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from './store';

// Use throughout your app instead of plain 'useDispatch' and 'useSelector'
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useAutoSaveOnPageLeave = (
    stateDependencies: DependencyList, 
    saveFunction: () => Promise<void>, 
    pageLeaveInidcators: DependencyList, 
    loadFunction?: () => Promise<void>,
    autoSaveOnChange: boolean = false
    ) => {
    const saveRef = useRef<() => void>();

    /**
     * Using a ref for saving data and updating it via effect hook is the
     * only reliable way to make sure that load / save only triggers
     * when required (navigation + app suspend). It ensures that the
     * load / save hook
     * 
     *   a) always has access to the current save function
     *      (using the current states)
     *   b) only triggers on page leaves and has no 
     *      inner dependency to the states
     */

    // update saveRef when state changes
    useEffect(() => {
        if (saveRef.current) {
            document.removeEventListener('pause', saveRef.current);
        }
        saveRef.current = saveFunction;
        document.addEventListener('pause', saveRef.current);
    }, stateDependencies);

    // optionally auto-save when any dependency changes
    useEffect(() => {
        if (autoSaveOnChange) {
            saveFunction(); // maybe debounce
        }
    }, autoSaveOnChange ? stateDependencies : []);

    // load data when entering page, save data when leaving page
    useEffect(() => {
        // load data
        if (loadFunction) {
            loadFunction();
        }

        // set up save action on navigate
        return () => {
            if (saveRef.current) {
                saveRef.current();
            }
        };
    }, pageLeaveInidcators);
};

export const useParentContainerDisableScrolling = () => {
    useEffect(() => {
        const parentContainer = document.getElementsByClassName('content-container content-module')[0] as HTMLDivElement;
        if (parentContainer) {
            // disable overflow of parent container for dragging to work on iOS
            parentContainer.style.overflow = 'hidden';
        }
        return () => {
            if (parentContainer) {
                parentContainer.style.overflow = '';
            }
        };
    });
};